import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dialog, DialogContent, DialogProps, IconButton, Stack, Typography } from '@mui/material'
import Otp from '../Otp'
import { addMobileNumber, resendVerificationCode, verifyMobileNumber } from '../../services/apis/user'
import commonActions from '../../actions/common.actions'
import { PhoneInput } from './PhoneInput'
import { primary } from '../../assets/colors'
import { User } from '../../proto/generated/identity'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { setUserDetails } from '../../reducer/user.reducer'
import { selectUserDetails } from '../../selectors/user'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import ClearIcon from '../../assets/icons/ClearIcon'
import { updateDefaultScalperSettings } from '../../util/scalper'


const MobileVerificationAlert = () => {
    const userDetails = useSelector(selectUserDetails)
    const [open, setOpen] = useState<boolean>(false)
    const [mobileNumber, setMobileNumber] = useState('')
    const [countryCode, setCountryCode] = useState('91')
    const [iso2, setIso2] = useState('in')
    const [otp, setOtp] = useState('')
    const [otpView, setOtpView] = useState(false)
    const dispatch = useDispatch()
    const [showInvalidOtpError, setShowInvalidOtpError] = useState(false)
    const [timer, setTimer] = useState(60) // Initial timer value in seconds (2 minutes)
    const [isTimerRunning, setIsTimerRunning] = useState(false)
    const [requestId, setRequestId] = useState<string>('')

    const setUser = useCallback((user: User | undefined) => {
        if (user) {
            dispatch(setUserDetails({
                ...userDetails,
                currentUser: user
            }))
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Mobile number verified successfully',
                errorType: 'success'
            })
        }
    }, [dispatch, userDetails])

    const handleVerify = useCallback(async () => {
        if (otpView) {
            AnalyticsManager.logEvent('Verify OTP Clicked', { otp: otp })
            try {
                const response = await verifyMobileNumber(
                    {
                        body: {
                            requestId: requestId,
                            verificationCode: otp
                        }
                    }
                )
                const user = await updateDefaultScalperSettings(response);
                setUser(user)
            } catch (error: any) {
                setShowInvalidOtpError(true)
                console.error('Error', error)
            }
        } else {
            AnalyticsManager.logEvent('Verify Mobile Number Clicked', { mobile_number: `${countryCode}-${mobileNumber}` })
            try {
                const response = await addMobileNumber(
                    {
                        body: {
                            countryCode: countryCode,
                            mobileNumber: mobileNumber
                        }
                    }
                )
                if (response.verificationRequired) {
                    setRequestId(response.requestId)
                    setOtpView(true)
                } else {
                    setUser(response.user)
                }
            } catch (error) {
                console.log('Something went wrong', error)
            }
        }
    }, [otpView, otp, setUser, countryCode, mobileNumber, requestId])

    const handleClose = useCallback((e: any) => {
        setOpen(false)
    }, [])

    useEffect(() => {
        if (userDetails && userDetails.currentUser && !userDetails.currentUser.mobileVerified) {
            setOpen(true)
        } else {
            setOpen(false)
        }
    }, [userDetails])

    const handleBackClick = useCallback(() => {
        AnalyticsManager.logEvent('Wrong Number Clicked')
        setOtpView(false)
        setOtp('')
    }, [])

    useEffect(() => {
        let interval: any
        if (isTimerRunning) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1)
            }, 1000)
        }
        return () => clearInterval(interval)
    }, [isTimerRunning])


    useEffect(() => {
        if (showInvalidOtpError) {
            setShowInvalidOtpError(!showInvalidOtpError)
        }
    }, [otp, showInvalidOtpError])

    const formatTime = useCallback((time: number) => {
        const minutes = Math.floor(time / 60)
        const seconds = time % 60
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }, [])

    useEffect(() => {
        if (timer === 0) {
            setIsTimerRunning(false) // Stop the timer when countdown reaches zero
        }
    }, [timer])

    const handleResendOTP = useCallback(async () => {
        AnalyticsManager.logEvent('Resend OTP Clicked')
        const response = await resendVerificationCode()
        setRequestId(response.requestId)
        setTimer(60)
        setIsTimerRunning(true)
    }, [])

    useEffect(() => {
        if (otpView) {
            setTimer(60)
        }
        setIsTimerRunning(true)
    }, [otpView])

    const styles = useMemo(() => ({
        paper: {
            minHeight: '25%',
            minWidth: '30%',
            padding: '32px'
        }
    }), [])

    useEffect(() => {
        if (otp.length === 6) {
            handleVerify()
        }
    }, [otp])

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={{ style: styles.paper }}>
            {!otpView && <DraggableDialogTitle>Add your mobile number</DraggableDialogTitle>}
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 12
                }}
            >
                <ClearIcon fontSize={"small"} />
            </IconButton>
            <DialogContent>
                <Stack justifyContent={'center'} alignItems={'center'}>
                    <Typography paddingBottom={2} variant={'body2'}
                                textAlign={'center'}>{!otpView ? '' : `Enter the verification code sent to +${countryCode}-${mobileNumber}`}</Typography>
                </Stack>
                <Stack>
                    {otpView ? <Stack paddingBottom={2} alignItems={'center'}><Button variant={'text'}
                                                                                      onClick={handleBackClick}>Wrong
                        Number?</Button></Stack> : null}
                    {otpView ? <Otp otp={otp} setOtp={(val: string) => setOtp(val)} /> :
                        <PhoneInput selectedCode={iso2} value={mobileNumber} onChange={(data) => {
                            setCountryCode(data.country.dialCode)
                            setMobileNumber(data.phone)
                            setIso2(data.country.iso2)
                        }} />
                    }
                    {
                        otpView && <Stack visibility={showInvalidOtpError ? 'visible' : 'hidden'} marginTop={4}
                                          alignItems={'center'}>
                            <Typography variant={'body2'} fontWeight={'bolder'} color={'error'}>Invalid OTP
                                Entered</Typography>
                        </Stack>
                    }
                    {otpView ?
                        <Stack padding={'32px'} justifyContent={'space-around'} alignItems={'center'}>
                            <Typography variant={'caption'}>Didn't receive an OTP?</Typography>
                            <div>
                                {timer > 0 ? (
                                    <span className="font-feature-num"
                                          style={{ color: 'textSecondary', fontSize: '12px' }}>Resend OTP in: <span
                                        style={{ color: primary }}>{formatTime(timer)}</span></span>
                                ) : <Button sx={{ padding: '2px', fontSize: '12px' }} disabled={isTimerRunning}
                                            onClick={handleResendOTP}>Resend OTP</Button>}
                            </div>
                        </Stack> : null}
                    <Button style={{ marginTop: '16px' }} fullWidth={true}
                            variant="contained"
                            disabled={(countryCode === '91' && mobileNumber.length !== 10) || (otpView && otp.length !== 6) || showInvalidOtpError || !mobileNumber}
                            onClick={handleVerify}>
                        Verify
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default memo(MobileVerificationAlert)